import type { ProcessWorkflowTimelineItem } from '@cartwheel/api/v1'

type StepStatus = 'completed' | 'running' | 'failed' | 'pending'

const SUFFIX_STATUS: ReadonlyArray<readonly [string, StepStatus]> = [
  ['_failed', 'failed'],
  ['_timed_out', 'failed'],
  ['_canceled', 'failed'],
  ['_terminated', 'failed'],
  ['_completed', 'completed'],
  ['_signaled', 'completed'],
  ['_fired', 'completed'],
  ['_started', 'running'],
]

function stepStatusFor(eventType: string | null | undefined): StepStatus {
  if (!eventType) return 'pending'
  return SUFFIX_STATUS.find(([suffix]) => eventType.endsWith(suffix))?.[1] ?? 'pending'
}

const STEP_DOT_CLASS: Record<StepStatus, string> = {
  completed: 'bg-mint',
  running: 'bg-sea ring-4 ring-sky-25',
  failed: 'bg-orange-500',
  pending: 'border border-charcoal-25 bg-white',
}

const STEP_ICON_COLOR: Record<StepStatus, string> = {
  completed: 'text-mint',
  running: 'text-sea',
  failed: 'text-orange-500',
  pending: 'text-charcoal-25',
}

// event_types that render as a Font Awesome icon instead of a colored dot.
// Falls through to the dot for anything not in this map.
const EVENT_ICON: Record<string, string> = {
  timer_started: 'fa-hourglass-start',
  timer_fired: 'fa-hourglass-end',
  timer_canceled: 'fa-ban',
}

// `timeZoneName: 'short'` mirrors `packages/components/src/utilities/format-date-time.ts`,
// our shared convention for surfacing the viewer's tz abbreviation (EST/EDT/PST/…).
function formatDateTime(dateStr: string | null | undefined): string {
  if (!dateStr) return '—'
  return new Date(dateStr)
    .toLocaleString('en-US', {
      month: 'numeric',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZoneName: 'short',
    })
    .replace(/,/g, '')
}

function ProcessWorkflowTimelineRow({ item }: { item: ProcessWorkflowTimelineItem }) {
  const status = stepStatusFor(item.event_type)
  const iconClass = item.event_type ? EVENT_ICON[item.event_type] : undefined

  return (
    <li className="flex items-center gap-3 py-2">
      {iconClass ? (
        <i
          role="img"
          aria-label={`${status} step`}
          className={`fa-solid ${iconClass} w-3 flex-shrink-0 text-center text-sm ${STEP_ICON_COLOR[status]}`}
        />
      ) : (
        <span
          role="img"
          aria-label={`${status} step`}
          className={`inline-block h-3 w-3 flex-shrink-0 rounded-full ${STEP_DOT_CLASS[status]}`}
        />
      )}
      <div className="min-w-0 flex-1 truncate text-sm text-charcoal">{item.label}</div>
      {item.occurred_at && (
        <span className="whitespace-nowrap text-xs text-charcoal-50">
          {formatDateTime(item.occurred_at)}
        </span>
      )}
    </li>
  )
}

export interface ProcessWorkflowTimelineProps {
  items: ProcessWorkflowTimelineItem[]
}

export function ProcessWorkflowTimeline({ items }: ProcessWorkflowTimelineProps) {
  if (items.length === 0) {
    return <p className="text-sm text-charcoal-50">No activity yet.</p>
  }
  return (
    <ol className="divide-y divide-gray-stroke">
      {items.map((item) => (
        <ProcessWorkflowTimelineRow key={item.event_id} item={item} />
      ))}
    </ol>
  )
}
